import {
    Component,
    effect,
    input,
    output,
    ChangeDetectionStrategy
} from "@angular/core";
import { FormGroup, ReactiveFormsModule } from "@angular/forms";
import { debounceTime } from "rxjs/internal/operators/debounceTime";
import { distinctUntilChanged } from "rxjs/internal/operators/distinctUntilChanged";
import { takeUntil } from "rxjs/internal/operators/takeUntil";
import { Subject } from "rxjs/internal/Subject";
import { CardComponent } from "~app/common/components/cards/card/card.component";
import { DatepickerComponent } from "~app/common/components/datepicker/datepicker.component";
import { HeaderComponent } from "~app/common/components/header/header.component";
import { RadiobuttonGroupComponent } from "~app/common/components/radiobuttongroup/radiobuttongroup.component";
import { DisabledControlDirective } from "~app/common/directives/disabled-control/disabled-control.directive";
import { AuthenticationService } from "~app/common/services/authentication/authentication.service";
import { FormBinderService } from "~app/common/services/form-binder.service";
import { ValidationErrorLevels } from "~app/common/validation/ValidationErrorLevels";
import { ValidationScopes } from "~app/common/validation/ValidationScopes";
import { AtgardNaturvardesbedomningDTO } from "../../../../models/naturvardesbedomning-models";
import { ForenkladRiskbedomningValidation } from "./forenklad-riskbedomning.validation";

@Component({
    selector: "rask-forenklad-riskbedomning",
    imports: [
        CardComponent,
        DatepickerComponent,
        ReactiveFormsModule,
        DisabledControlDirective,
        HeaderComponent,
        RadiobuttonGroupComponent
    ],
    templateUrl: "./forenklad-riskbedomning.component.html",
    changeDetection: ChangeDetectionStrategy.Eager,
    styleUrl: "./forenklad-riskbedomning.component.scss"
})
export class ForenkladRiskbedomningComponent {
    naturvardesbedomning = input<AtgardNaturvardesbedomningDTO>();
    isReadOnly = input<boolean>(false);
    updateDTO = output();

    protected form: FormGroup;

    protected validationContext = ForenkladRiskbedomningValidation.init(() => this.naturvardesbedomning());
    private readonly formRebind$ = new Subject<void>();

    constructor(
        private readonly formBinder: FormBinderService,
        private readonly authService: AuthenticationService) {
        effect(() => {
            if (!this.naturvardesbedomning()) {
                return;
            }
            this.bindForm();
        });
    }

    protected isFormValid() {
        return this.validationContext.validate(true);
    }

    protected canComplete() {
        return !this.isReadOnly() && this.isFormValid();
    }

    protected setUtfordAv(): void {
        this.form.controls.utfordDatum.patchValue(new Date());
        this.form.controls.utfordAv.patchValue(this.authService.getUserData().fullUserName);
    }

    protected resetUtfordAv(): void {
        this.form.controls.utfordDatum.patchValue(null);
        this.form.controls.utfordAv.patchValue(null);
    }

    private bindForm(): void {
        if (!this.form) {
            this.form = this.formBinder.CreateGroup(this.naturvardesbedomning());
        } else {
            this.formRebind$.next();
            this.formBinder.reBindForm(this.form, this.naturvardesbedomning());
        }
        this.formBinder.attachValidation(this.validationContext.current, this.form);
        this.validationContext.scope(ValidationScopes.spara, ValidationErrorLevels.error);
        this.detectAllChanges();
        if (this.isReadOnly()) {
            this.form.disable();
        }
    }

    private detectAllChanges(): void {
        if (!this.form) {
            return;
        }
        this.form.valueChanges
            .pipe(
                debounceTime(200),
                distinctUntilChanged(),
                takeUntil(this.formRebind$)
            ).subscribe((change: AtgardNaturvardesbedomningDTO) => {
                this.onFormChanges(change);
            });
    }

    private readonly onFormChanges = (change: AtgardNaturvardesbedomningDTO): void => {
        if (!change.faltbesokKravs) {
            this.naturvardesbedomning().faltbesokDatum = null;
        }
        this.validationContext.validate(true);
        this.updateDTO.emit();
    };
}
